// src/domains/dash.upstream.ts —— 06 C-18 上游 API / 商户钱包原生 renderVals（经 domains/dash.ts 分派器挂载）+ C-24 顶栏上游状态章数据源。
// enter 并发拉 wallet / credentials / endpoint-health?window=24h / wallet-logs，四块各自独立成败（任一失败不清空其余卡片）；
// 数据存模块私有 WeakMap<host, UpRun>，reset 换代即丢弃在途结果；刷新经 ds.rev 递增触发重渲染。异步回调内文案以 gp.ui.dict() 重取（切换语言后不回落挂载时字典）
import type { DcHost, Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtAmount } from '../../../shared/src/money';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { fmtDateTime } from '../../../shared/src/time';
import type { EndpointHealth, HealthSummary, TestWebhookResult, UpCredentials, WalletLogsPage, WalletRow } from '../pages/payout.types';
import { gate } from '../perm';
import type { AdminState } from '../state';
import type { DashPart } from './dash';
import { str, type AdminCtx } from './types';

const EMPTY = '—';
const RATE_RE = /^(\d+)(?:\.(\d{1,4}))?$/;

// 后端 okRate 为 half-up 四舍五入到 4 位（upops/service.go okRate、health.go rate）：样本数 < 20000 时任一失败至多得 "0.9999"，
// 故 "1.0000" 仅在已知样本数 < 20000 时可断定为全部成功
const EXACT_MAX_CALLS = 20_000;

// 0~1 比例串 → 百分比展示（06 C-18 / spec §4.7 B5）：两位小数截断、去尾零（原型「99.6%」「100%」口径）。
// "1.0000" 显示约定：calls 已知且 < 20000 → 「100%」；样本数未知（凭据 webhook_ok_rate_24h）或 ≥ 20000 → 「>99.99%」（真实比例 ≥ 99.995%，不冒充 100%）。
// null / 缺失 → 「—」；非法值原样显示
export function ratePct(raw: string | null | undefined, calls?: number): string {
  if (raw === null || raw === undefined) return EMPTY;
  const m = RATE_RE.exec(raw.trim());
  if (!m || m[1] === undefined) return raw;
  const bp = BigInt(m[1]) * 10_000n + BigInt((m[2] ?? '').padEnd(4, '0')); // 万分比
  if (bp >= 10_000n) return calls !== undefined && calls < EXACT_MAX_CALLS ? '100%' : '>99.99%';
  const frac = String(bp % 100n).padStart(2, '0').replace(/0+$/, '');
  return `${bp / 100n}${frac ? `.${frac}` : ''}%`;
}

// 低于 95%（06 C-18 接口健康成功率红字阈值）
export function rateBelow95(raw: string | null | undefined): boolean {
  const m = raw ? RATE_RE.exec(raw.trim()) : null;
  if (!m || m[1] === undefined) return false;
  return BigInt(m[1]) * 10_000n + BigInt((m[2] ?? '').padEnd(4, '0')) < 9_500n;
}

function money(v: string | null | undefined, sign = false): string {
  if (v === null || v === undefined || v === '') return EMPTY;
  try {
    const s = fmtAmount(v, 2);
    return sign && !s.startsWith('-') && s !== '0.00' ? `+${s}` : s;
  } catch {
    return v;
  }
}

// 上游钱包流水时间为上游原文（`YYYY-MM-DD HH:mm:ss`，不带时区）：取到分钟原样展示；ISO 串按配置时区格式化
function upTime(v: string, tz: string | undefined): string {
  const s = v.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) return s.slice(0, 16);
  const f = fmtDateTime(s, { tz });
  return f === EMPTY ? s || EMPTY : f;
}

// ---- 每宿主运行代 ----

type Slot<T> = { st: 'idle' | 'loading' | 'ok' | 'err'; data: T | null };
const idle = <T>(): Slot<T> => ({ st: 'idle', data: null });

interface Slots {
  wallet: Slot<readonly WalletRow[]>;
  creds: Slot<UpCredentials>;
  health: Slot<EndpointHealth>;
  logs: Slot<WalletLogsPage>;
}

interface UpRun {
  readonly epoch: number;
  readonly slots: Slots;
  readonly busy: Set<string>; // 在途锁：sync / test
}

const runs = new WeakMap<object, UpRun>();

function fresh(host: object): UpRun {
  const r: UpRun = { epoch: (runs.get(host)?.epoch ?? 0) + 1, slots: { wallet: idle(), creds: idle(), health: idle(), logs: idle() }, busy: new Set() };
  runs.set(host, r);
  return r;
}

function runOf(host: object): UpRun {
  return runs.get(host) ?? fresh(host);
}

function bump(host: DcHost<AdminState>): void {
  host.setState({ ds: { rev: host.state.ds.rev + 1 } });
}

// 单块加载：结果写入前核对运行代（reset / 换人后丢弃）；失败只置本块错误态并 toast（TD-846），不影响其余三块
function load<T>(ctx: AdminCtx, run: UpRun, put: (s: Slot<T>) => void, fetch: () => Promise<T>): void {
  const host = ctx.host;
  const current = (): boolean => runs.get(host) === run;
  put({ st: 'loading', data: null });
  void fetch()
    .then((data) => {
      if (!current()) return;
      put({ st: 'ok', data });
      bump(host);
    })
    .catch((e: unknown) => {
      if (!current()) return;
      put({ st: 'err', data: null });
      bump(host);
      ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

function enter(ctx: AdminCtx): void {
  const run = fresh(ctx.host);
  const api = ctx.gp.api;
  const sl = run.slots;
  load(ctx, run, (x) => void (sl.wallet = x), () => api.call<readonly WalletRow[]>('GET /api/admin/upstream/wallet'));
  load(ctx, run, (x) => void (sl.creds = x), () => api.call<UpCredentials>('GET /api/admin/upstream/credentials'));
  load(ctx, run, (x) => void (sl.health = x), () => api.call<EndpointHealth>('GET /api/admin/upstream/endpoint-health', { query: { window: '24h' } }));
  load(ctx, run, (x) => void (sl.logs = x), () => api.call<WalletLogsPage>('GET /api/admin/upstream/wallet-logs', { query: { page: 1, size: 20 } }));
  bump(ctx.host);
}

function reset(host: object): void {
  fresh(host);
}

// 在途锁（每宿主 × 动作键）；结束即释放
async function locked(run: UpRun, key: string, fn: () => Promise<void>): Promise<void> {
  if (run.busy.has(key)) return;
  run.busy.add(key);
  try {
    await fn();
  } finally {
    run.busy.delete(key);
  }
}

// 立即同步：wallet?sync=1（上游失败 1403，快照不写）→ 迟到结果按运行代丢弃 → toast toastSync（06 C-18）
export async function syncWallet(ctx: AdminCtx): Promise<void> {
  const host = ctx.host;
  const run = runOf(host);
  await locked(run, 'sync', async () => {
    try {
      const rows = await ctx.gp.api.call<readonly WalletRow[]>('GET /api/admin/upstream/wallet', { query: { sync: 1 } });
      if (runs.get(host) !== run) return;
      run.slots.wallet = { st: 'ok', data: rows };
      bump(host);
      ctx.gp.ui.toast(str(ctx.gp.ui.dict(), 'toastSync'));
    } catch (e) {
      if (runs.get(host) === run) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    }
  });
}

// 发送测试回调（logs.edit）：无权限时只提示权限文案（模板按钮无样式绑定，置灰以提示代替，W-8）；1503 = prod / 未配测试密钥
export async function testHook(ctx: AdminCtx): Promise<void> {
  const g = gate(ctx.s.sh.me, ctx.t, 'logs', 'edit');
  if (g.disabled) {
    ctx.gp.ui.toast(g.title);
    return;
  }
  const host = ctx.host;
  const run = runOf(host);
  await locked(run, 'test', async () => {
    try {
      const r = await ctx.gp.api.call<TestWebhookResult>('POST /api/admin/upstream/test-webhook', { body: {} });
      if (runs.get(host) !== run) return;
      ctx.gp.ui.toast(interpolate(str(ctx.gp.ui.dict(), 'toastTest'), { http_status: r.http_status }));
    } catch (e) {
      if (runs.get(host) === run) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    }
  });
}

function walletUsd(rows: readonly WalletRow[] | null): WalletRow | null {
  if (!rows || rows.length === 0) return null;
  return rows.find((r) => r.currency.toUpperCase() === 'USD') ?? rows[0] ?? null;
}

const orDash = (v: string | null | undefined): string => (v ? v : EMPTY);

function vals(ctx: AdminCtx): Record<string, unknown> {
  const t: Dict = ctx.t;
  const run = runs.get(ctx.host);
  const w = walletUsd(run?.slots.wallet.data ?? null);
  const c = run?.slots.creds.data ?? null;
  const h = run?.slots.health.data ?? null;
  const logs = run?.slots.logs.data ?? null;
  const settled = (s: Slot<unknown> | undefined): boolean => s?.st === 'ok' || s?.st === 'err';
  const endpoints = (h?.list ?? []).map((e) => ({
    m: e.method,
    path: e.endpoint,
    calls: e.calls,
    p95: e.p95_ms,
    ok: ratePct(e.ok_rate, e.calls),
    // 原型 L960 样式串逐字
    mStyle: `font-weight:800;font-size:11px;color:${e.method === 'GET' ? '#0d9a68' : '#6b4fd8'}`,
    okStyle: `font-weight:700;text-align:right;color:${rateBelow95(e.ok_rate) ? '#d1434b' : '#0d9a68'}`,
  }));
  const tz = ctx.s.sh.timezone || undefined;
  const merchantLedger = (logs?.list ?? []).map((l) => {
    const amount = money(l.amount, true);
    return {
      title: l.title_key || l.type,
      amount,
      after: money(l.balance_after),
      time: upTime(l.created_at, tz),
      // 原型 L959 样式串逐字（正数绿）
      amtStyle: `font-weight:700;color:${amount.startsWith('+') ? '#0d9a68' : '#0f1a17'}`,
    };
  });
  const synced = w ? fmtDateTime(w.snapshot_at, { tz, seconds: true }) : EMPTY;
  return {
    up: {
      balance: w ? `$${money(w.balance)}` : EMPTY,
      frozen: w ? `$${money(w.frozen)}` : EMPTY,
      syncedAt: synced === EMPTY ? EMPTY : synced.slice(11),
      hookUrl: orDash(c?.webhook_url),
      success: c ? ratePct(c.webhook_ok_rate_24h) : EMPTY,
    },
    // 原型 L957 四行标签逐字
    creds: [
      { k: 'Base URL', v: orDash(c?.base_url) },
      { k: 'access_code', v: orDash(c?.access_code_masked) },
      { k: 'Private key', v: orDash(c?.private_key_masked) },
      { k: 'Webhook public key', v: orDash(c?.webhook_public_key_masked) },
    ],
    endpoints,
    merchantLedger,
    // 加载完成（成功无行或失败）才出空态；钱包流水失败 = 空态 + err1403 toast（模板空态块无红字槽位，W-8）
    endpointsEmpty: settled(run?.slots.health) && endpoints.length === 0,
    ledgerEmpty: settled(run?.slots.logs) && merchantLedger.length === 0,
    rotateKeys: () => ctx.gp.ui.toast(str(ctx.gp.ui.dict(), 'toastRotate')), // 提示性按钮（审计 A-6），不调后端
    testHook: () => void testHook(ctx),
    syncWallet: () => void syncWallet(ctx),
  };
}

export const DASH_UPSTREAM: DashPart = { vals, enter, reset };

// ==================== C-24 顶栏上游状态章（endpoint-health?window=5m 的 summary，60s 轮询） ====================

export type UpTone = 'ok' | 'warn' | 'down';

const CHIP_MS = 60_000; // 06 C-24「60s 轮询 [本册定]」
const DOWN_STREAK = 5; // 06 C-24：最近连续 5 次上游调用均失败（04 §6.1 告警阈值）

// 06 C-24 判定（按优先级）：连续失败 ≥5 → 异常；5 分钟成功率 <95% → 波动；≥95% 或窗口内无调用 → 正常
export function toneOf(s: HealthSummary): UpTone {
  if (s.consecutive_failures >= DOWN_STREAK) return 'down';
  if (s.ok_rate !== null && rateBelow95(s.ok_rate)) return 'warn';
  return 'ok';
}

// 原型状态章样式串逐字（绿）；红 / 琥珀两档取 06 C-24 色值，结构不变
const CHIP_COLORS: Readonly<Record<UpTone, readonly [string, string, string]>> = {
  ok: ['#e3f6ee', '#0d9a68', 'upstreamOk'],
  warn: ['#fff3df', '#c47a0c', 'upstreamWarn'],
  down: ['#fdecec', '#d1434b', 'upstreamDown'],
};

interface ChipRun {
  tone: UpTone;
  warned: boolean; // 失败告警每会话一次
  poller: Poller | null;
  off: (() => void) | null;
}

const chips = new WeakMap<object, ChipRun>();

async function chipTick(ctx: AdminCtx, run: ChipRun): Promise<void> {
  const host = ctx.host;
  if (chips.get(host) !== run || host.state.screen !== 'app') return;
  try {
    // 60s 定时轮询：passive，不刷新服务端最后活跃（空闲超时判定）
    const r = await ctx.gp.api.call<EndpointHealth>('GET /api/admin/upstream/endpoint-health', { query: { window: '5m' }, passive: true });
    if (chips.get(host) !== run) return;
    const tone = toneOf(r.summary);
    if (tone !== run.tone) {
      run.tone = tone;
      bump(host);
    }
  } catch (e) {
    // 失败保持当前样式（首次失败即原型绿色），每会话告警一次
    if (chips.get(host) !== run || run.warned) return;
    run.warned = true;
    console.warn('[gp:admin] 上游状态章取数失败', e);
  }
}

export function stopUpChip(host: object): void {
  const run = chips.get(host);
  if (!run) return;
  run.poller?.stop();
  run.off?.();
  chips.delete(host);
}

// session:ready 时启动（换代）；session:lost 自行停止并复位为原型绿色
export function startUpChip(ctx: AdminCtx): void {
  stopUpChip(ctx.host);
  const run: ChipRun = { tone: 'ok', warned: false, poller: null, off: null };
  chips.set(ctx.host, run);
  run.off = ctx.events.on('session:lost', () => stopUpChip(ctx.host));
  run.poller = createPoller(() => chipTick(ctx, run), CHIP_MS);
  run.poller.start();
}

export function upChipVals(ctx: AdminCtx): { style: string; dot: string; text: string } {
  const [bg, fg, key] = CHIP_COLORS[chips.get(ctx.host)?.tone ?? 'ok'];
  return {
    style: `display:flex;align-items:center;gap:6px;font-size:12px;padding:0 10px;height:32px;border-radius:999px;background:${bg};color:${fg};font-weight:700;white-space:nowrap`,
    dot: `width:8px;height:8px;border-radius:50%;background:${fg};display:inline-block`,
    text: str(ctx.t, key),
  };
}
